import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export interface TourStep {
  id: string;
  target: string;
  title: string;
  content: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  action?: 'click' | 'wait' | 'navigate' | 'next';
  navigationPath?: string;
  delay?: number;
  spotlight?: boolean;
  waitForElement?: boolean;
}

interface TourContextValue {
  isActive: boolean;
  isVisible: boolean;
  currentStep: number;
  totalSteps: number;
  currentStepData: TourStep | null;
  startTour: () => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  handleTargetClick: (targetId: string) => void;
  advanceTourAfterNavigation: () => void;
}

const tourSteps: TourStep[] = [
  // Step 1: Welcome on HomePage
  {
    id: 'welcome-home',
    target: 'home-header',
    title: 'Welcome to Thriftalyze! 🎉',
    content: "Welcome to the future of item analysis! This beautiful interface is designed to make discovering the value of your items both fun and effortless. Let's take a guided tour through all the amazing features.",
    position: 'bottom',
    action: 'wait',
    delay: 4000,
    spotlight: true
  },

  // Step 2: Highlight Start Analyzing Button
  {
    id: 'start-analyzing-cta',
    target: 'tour-start-analyzing',
    title: 'Jump Right In! 🚀',
    content: 'Ready to start immediately? Click this button to jump straight to the camera and begin analyzing your first item. Perfect for when you know exactly what you want to do!',
    position: 'bottom',
    action: 'next',
    spotlight: true
  },

  // Step 3: Navigate to Analyze
  {
    id: 'analyze-navigation',
    target: 'tour-analyze-nav',
    title: 'Choose Your AI Provider',
    content: 'For a more guided experience, start here! Click "Analyze" to choose from different AI analysis providers. Each offers unique strengths for item identification and valuation.',
    position: 'right',
    action: 'click',
    spotlight: true
  },

  // Step 4: On Analyze Page - Provider Selection
  {
    id: 'provider-selection',
    target: 'tour-provider-selection',
    title: 'AI Analysis Options',
    content: 'Here you can explore different AI providers including Gemini, GPT-4 Vision, and Claude. Each has unique capabilities - some excel at antiques, others at modern items or collectibles.',
    position: 'top',
    action: 'next',
    waitForElement: true
  },

  // Step 5: Select Gemini AI
  {
    id: 'select-gemini',
    target: 'tour-gemini-analysis',
    title: 'Let\'s Use Gemini AI ✨',
    content: 'For this demo, we\'ll use Google\'s Gemini AI. It provides excellent item identification, detailed descriptions, and reliable value estimates. Click here to select it!',
    position: 'right',
    action: 'click',
    spotlight: true
  },

  // Step 6: Navigate to Camera Page
  {
    id: 'camera-page',
    target: 'tour-camera-page-container',
    title: 'Upload Your Image 📸',
    content: 'Perfect! Now you\'re on the camera page where you can capture or upload images for analysis. The interface is designed to make photo capture and upload as smooth as possible.',
    position: 'top',
    action: 'next',
    waitForElement: true,
    navigationPath: '/analyze'
  },

  // Step 7: Upload Image
  {
    id: 'upload-image',
    target: 'tour-upload-image',
    title: 'Upload Your Image 📸',
    content: 'Click here to upload an image from your device or take a photo. The AI works best with clear, well-lit photos that show the item clearly. Choose an image of something you\'d like to analyze!',
    position: 'top',
    action: 'click',
    spotlight: true,
    navigationPath: '/analyze'
  },

  // Step 8: Start Analysis
  {
    id: 'start-analysis',
    target: 'tour-analyze-image',
    title: 'Analyze Your Item 🔍',
    content: 'Once you have an image ready, click this button to start the AI analysis. Gemini will identify your item, provide detailed insights, and estimate its value!',
    position: 'top',
    action: 'click',
    spotlight: true,
    navigationPath: '/analyze'
  },

  // Step 9: Analysis Results
  {
    id: 'analysis-results',
    target: 'tour-analysis-results',
    title: 'Your Analysis Results! 🎯',
    content: 'Amazing! Here are your complete analysis results with item identification, detailed description, estimated value, condition assessment, and market insights. This is where the magic happens!',
    position: 'top',
    action: 'next',
    waitForElement: true,
    navigationPath: '/analyze'
  },

  // Step 10: Save Analysis
  {
    id: 'save-analysis',
    target: 'tour-save-analysis',
    title: 'Save Your Discovery 💾',
    content: 'Found something valuable or interesting? Click here to save this analysis to your favorites. This makes it easy to track valuable items and build your collection history.',
    position: 'top',
    action: 'click',
    spotlight: true,
    navigationPath: '/analyze'
  },

  // Step 11: View History
  {
    id: 'view-history',
    target: 'tour-history-nav',
    title: 'Your Analysis History 📚',
    content: 'All your analyses are automatically saved here. Click "History" to see all your previous analyses, track trends over time, and revisit interesting discoveries.',
    position: 'right',
    action: 'click',
    spotlight: true
  },

  // Step 12: History Page
  {
    id: 'history-page',
    target: 'tour-history-list',
    title: 'Your Collection Journey',
    content: 'This is your personal analysis museum! Browse through all your past analyses, compare results, filter by date or value, and see how your collection knowledge has grown.',
    position: 'top',
    action: 'next',
    waitForElement: true
  },

  // Step 13: View Favorites
  {
    id: 'view-favorites',
    target: 'tour-favorites-nav',
    title: 'Your Favorites Collection ⭐',
    content: 'Items you\'ve favorited appear here. This is perfect for keeping track of your most valuable discoveries, wish list items, or pieces you want to research further.',
    position: 'right',
    action: 'click',
    spotlight: true
  },

  // Step 14: Return Home
  {
    id: 'return-home',
    target: 'tour-home-nav',
    title: 'Back to Dashboard 🏠',
    content: 'The homepage is your command center. From here you can quickly access all features, see your stats, check recent activity, and start new analyses.',
    position: 'right',
    action: 'click',
    spotlight: true
  },

  // Step 15: Demo Complete
  {
    id: 'demo-complete',
    target: 'home-header',
    title: 'Demo Complete! 🚀',
    content: 'Congratulations! You\'ve mastered Thriftalyze! You now know how to analyze items, view results, organize discoveries, and navigate the beautiful interface. Start analyzing your own items to uncover their hidden value!',
    position: 'bottom',
    action: 'next',
    navigationPath: '/'
  }
];

// Create TourContext using the imported createContext function
export const TourContext = React.createContext<TourContextValue | undefined>(undefined);

export const NewTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const startTour = useCallback(() => {
    console.log('🎯 Starting new tour...');
    setIsActive(true);
    setIsVisible(true);
    setCurrentStep(0);
    
    // Ensure we're on the home page
    if (window.location.pathname !== '/') {
      navigate('/');
    }
  }, [navigate]);

  const endTour = useCallback(() => {
    console.log('🎯 Ending tour');
    setIsActive(false);
    setIsVisible(false);
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback(() => {
    const step = tourSteps[currentStep];
    console.log(`🎯 Tour: Moving from step ${currentStep} (${step?.id}) to next step`);
    
    // Handle navigation before moving to next step
    if (step?.navigationPath && step.navigationPath !== window.location.pathname) {
      console.log(`🎯 Tour: Navigating to ${step.navigationPath}`);
      navigate(step.navigationPath);
    }
    
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      endTour();
    }
  }, [currentStep, navigate, endTour]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const skipTour = useCallback(() => {
    endTour();
  }, [endTour]);

  const handleTargetClick = useCallback((targetId: string) => {
    if (!isActive) return;
    
    const stepData = tourSteps[currentStep];
    if (!stepData) return;
    
    // Check if this is the correct target for the current step
    if (stepData.target === targetId && stepData.action === 'click') {
      console.log(`🎯 Tour: Target ${targetId} clicked`);
      
      // Special handling for demo workflow navigation and actions
      switch (targetId) {
        case 'tour-analyze-nav':
          console.log('🎯 Tour: Analyze nav clicked, navigating to analyze page');
          // Don't advance immediately - let the navigation handler manage this
          return;
          
        case 'tour-gemini-analysis':
          console.log('🎯 Tour: Gemini analysis selected, navigating to camera');
          // Navigate to Gemini analysis page and advance tour
          navigate('/analyze');
          setTimeout(() => {
            console.log('🎯 Tour: Advancing to next step after Gemini navigation');
            nextStep();
          }, 800);
          break;
          
        case 'tour-upload-image':
        case 'tour-analyze-image':
        case 'tour-save-analysis':
          console.log(`🎯 Tour: Demo action ${targetId} clicked, advancing tour`);
          nextStep();
          break;
          
        case 'tour-history-nav':
        case 'tour-favorites-nav':
        case 'tour-home-nav':
          console.log(`🎯 Tour: Navigation to ${targetId} clicked`);
          // Don't advance immediately - let the navigation handler manage this
          return;
          
        default:
          // For other click targets, advance immediately
          console.log(`🎯 Tour: Standard click target ${targetId}, advancing tour`);
          nextStep();
          break;
      }
    }
  }, [isActive, currentStep, nextStep, navigate]);

  const advanceTourAfterNavigation = useCallback(() => {
    if (!isActive) return;
    console.log('🎯 Tour: Advancing tour after navigation');
    nextStep();
  }, [isActive, nextStep]);

  // Handle auto-progression for wait steps (only for welcome step)
  useEffect(() => {
    if (!isActive || !isVisible) return;
    
    const step = tourSteps[currentStep];
    if (!step) return;
    
    // Only auto-advance for the welcome step (step 0)
    if (step.action === 'wait' && step.delay && currentStep === 0) {
      const timer = setTimeout(() => {
        nextStep();
      }, step.delay);
      
      return () => clearTimeout(timer);
    }
  }, [currentStep, isActive, isVisible, nextStep]);

  // Monitor route changes and validate tour step locations
  useEffect(() => {
    if (!isActive) return;
    
    const currentStepData = tourSteps[currentStep];
    if (!currentStepData?.navigationPath) return;
    
    const currentPath = location.pathname;
    const expectedPath = currentStepData.navigationPath;
    
    console.log('🎯 Tour: Route validation', { currentPath, expectedPath, step: currentStepData.id });
    
    // If user navigated away from the expected route, pause the tour or navigate back
    if (currentPath !== expectedPath) {
      console.log('🎯 Tour: User navigated away from expected route, pausing tour visibility');
      setIsVisible(false);
      
      // Optional: Auto-navigate back (uncomment if desired)
      // setTimeout(() => {
      //   console.log('🎯 Tour: Auto-navigating back to expected route');
      //   navigate(expectedPath);
      //   setIsVisible(true);
      // }, 1000);
    } else {
      // User is on the correct route, ensure tour is visible
      if (!isVisible) {
        console.log('🎯 Tour: User returned to expected route, showing tour');
        setIsVisible(true);
      }
    }
  }, [location.pathname, currentStep, isActive, isVisible, navigate]);

  const value = {
    isActive,
    isVisible,
    currentStep,
    totalSteps: tourSteps.length,
    currentStepData: tourSteps[currentStep] || null,
    startTour,
    endTour,
    nextStep,
    prevStep,
    skipTour,
    handleTargetClick,
    advanceTourAfterNavigation
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useNewTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useNewTour must be used within a NewTourProvider');
  }
  return context;
};
